// @flow

import React, { useState, useRef, useEffect } from "react";
import { localize } from "redux-i18n";

import { Button, SchemaForm, View, Text } from "@components";
import type { UserAccount } from "@modules/employee/reducer/user-account-details/types";
import type { PropertiesSchema } from "@types";
import uiSchema from "./ui-schema";
import EmployeeTypeExposure from "../employee-type-exposure";

type OwnProps = $Exact<{
  data: UserAccount,
  onSubmit: (data: UserAccount) => void,
  schema: ?PropertiesSchema,
  isLeaver: boolean,
}>;

type Props = { ...PropsFromLocalize, ...OwnProps };

function EditAccessLevel({ data, onSubmit, schema, isLeaver, t }: Props) {
  const formRef = useRef(null);
  const [edit, setEdit] = useState(false);
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    setFormData(_ => data);
  }, [data]);

  function handleSubmit(formData: Object) {
    onSubmit(formData);
    toggleEdit();
  }

  function submitForm() {
    formRef.current.submit();
  }

  function toggleEdit() {
    setEdit(_ => !edit);
  }

  function cancelEdit() {
    setFormData(_ => data);
    setEdit(false);
    formRef.handleCancel();
  }

  function handleChange(formData: { formData: UserAccount }) {
    setFormData(_ => formData);
  }

  const currentUserType = formData?.userType;

  return (
    <View position="relative">
      <View direction="row" justifyContent="space-between" separator="bottom" margin="0 0 0.5rem 0">
        <Text type="h6" weight="bold">
          {t("ACCESS_LEVEL")}
        </Text>
        {!isLeaver && (
          <>
            {!edit ? (
              <Button materialIcon="edit" className="small mb-1" onClick={toggleEdit}>
                {t("EDIT")}
              </Button>
            ) : (
              <View direction="row" gap={2} margin="0 0 0.25rem 0">
                <Button color="secondary" className="small" onClick={cancelEdit}>
                  {t("CANCEL")}
                </Button>
                <Button materialIcon="replay_circle_filled" className="small" onClick={submitForm}>
                  {t("SUBMIT")}
                </Button>
              </View>
            )}
          </>
        )}
      </View>
      {formData && (
        <SchemaForm
          ref={formRef}
          schema={schema}
          formData={formData}
          onSubmit={handleSubmit}
          readonly={!edit}
          uiSchema={uiSchema}
          onChange={handleChange}
          externalSubmit
        />
      )}
      {currentUserType && <EmployeeTypeExposure currentType={currentUserType} />}
    </View>
  );
}

export default localize()(EditAccessLevel);
